import useLocationStore from "@/store/locationStore";
import {
  default as MapView,
  Marker,
  Region,
} from "@teovilla/react-native-web-maps";
import { useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";

import { fetchPlacesByCoordinates } from "@/services/supabase-service";
import { usePlaceStore } from "@/store/place";
import { Place } from "@/types/type";

const Map = ({
  onMarkerSelect,
  onPress,
}: {
  onMarkerSelect: (place: Place) => void;
  onPress(coordinate: { latitude: number; longitude: number }): () => void;
}) => {
  const mapRef = useRef<MapView>(null);
  const {
    userLatitude,
    userLongitude,
    destinationLatitude,
    destinationLongitude,
    destinationCoordinates,
    setDestinationLocation,
  } = useLocationStore();

  const { places, setPlaces, selectedPlace } = usePlaceStore();

  const [isLoading, setIsLoading] = useState(false);
  const [currentRegion, setCurrentRegion] = useState<Region | null>(null);

  // Calculate the region based on the destination or user location
  const initialRegion =
    currentRegion ||
    (destinationLatitude && destinationLongitude
      ? {
          //Destination
          latitude: destinationLatitude,
          longitude: destinationLongitude,
          latitudeDelta: 0.0922,
          longitudeDelta: 0.0421,
        }
      : userLatitude && userLongitude
        ? {
            //User location
            latitude: userLatitude,
            longitude: userLongitude,
            latitudeDelta: 0.0922,
            longitudeDelta: 0.0421,
          }
        : {
            //Fallback region
            latitude: 39.9526,
            longitude: -75.1652,
            latitudeDelta: 0.0922,
            longitudeDelta: 0.0421,
          });

  const handleUserLocationPress = async (e) => {
    setIsLoading(true);
    console.log(e.nativeEvent);
    const userRegion = {
      latitude: e.nativeEvent.coordinate.latitude,
      longitude: e.nativeEvent.coordinate.longitude,
      latitudeDelta: 0.02,
      longitudeDelta: 0.02,
    };

    setDestinationLocation(
      e.nativeEvent.coordinate.latitude,
      e.nativeEvent.coordinate.longitude,
    );

    onPress(e.nativeEvent.coordinate);
    setIsLoading(false);
  };

  const handleRegionChangeComplete = async (
    region: Region,
    details: Details,
  ) => {
    if (details.isGesture) {
      setCurrentRegion(region); // Remember region as the user interacts
      const fetchedPlaces = await fetchPlacesByCoordinates(
        region.latitude,
        region.longitude,
        region.latitudeDelta,
        region.longitudeDelta,
      );
      setPlaces(fetchedPlaces);
    }
  };
  const handleMapReady = async () => {
    const fetchedPlaces = await fetchPlacesByCoordinates(
      initialRegion.latitude,
      initialRegion.longitude,
      initialRegion.latitudeDelta,
      initialRegion.longitudeDelta,
    );
    setPlaces(fetchedPlaces);
  };

  return (
    <View style={styles.map}>
      {isLoading ? (
        <View className="flex-1 justify-center items-center w-screen h-[350px]">
          <ActivityIndicator color="#003566" size="large" />
        </View>
      ) : (
        <MapView
          ref={mapRef}
          provider="google"
          googleMapsApiKey="AIzaSyBNp-wI4F0mqk-xwUWUU_wiQvOPU_-cvPs"
          style={styles.map}
          mapType="standard"
          initialRegion={initialRegion}
          region={currentRegion || initialRegion}
          showsUserLocation={true}
          showsMyLocationButton={true}
          onRegionChangeComplete={handleRegionChangeComplete}
          onMapReady={handleMapReady}
          onMapZoom={handleRegionChangeComplete}
          onPress={handleUserLocationPress}
        >
          {places.map((place, index) => {
            //console.log("marker here: ",place);
            return (
              <Marker
                key={index}
                coordinate={{
                  latitude: place.latitude,
                  longitude: place.longitude,
                }}
                title={place.title}
                description={place.description}
                onPress={() => {
                  console.log("Selected place:", place);
                  onMarkerSelect(place);
                }}
              />
            );
          })}

          {destinationCoordinates && selectedPlace == null && (
            <Marker
              coordinate={destinationCoordinates}
              title="Selected Location"
              description="This is your selected location"
            />
          )}
        </MapView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  map: {
    width: "100%",
    height: "100%",
  },
  marker: {
    backgroundColor: "blue",
    borderRadius: 10,
    padding: 5,
  },
  markerText: {
    color: "white",
    fontWeight: "bold",
    textAlign: "center",
  },
});

export default Map;
